import { Database, Model, Q } from '@nozbe/watermelondb';
import { isEmpty } from 'lodash';

import { HorsePayload } from '../../../../types/Horses';
import { LocationModel } from '../../../../types/Location';
import { ValidationOperation } from '../../../validation/validator';

export type HorseEntityTableName = 'horses' | 'locations' | 'horse_contacts';
export type HorseDependentEntityTableName =
    | 'entries'
    | 'entry_products'
    | 'entry_procedures'
    | 'event_horses';
export type HorsePreparedEntityTableName =
    | HorseEntityTableName
    | HorseDependentEntityTableName;

export type PreparedHorseData = {
    record: Model;
    table: HorsePreparedEntityTableName;
    operation: ValidationOperation;
};

const horsePreparedEntityTables: Record<HorsePreparedEntityTableName, true> = {
    horses: true,
    locations: true,
    horse_contacts: true,
    entries: true,
    entry_products: true,
    entry_procedures: true,
    event_horses: true,
};

export const mapRecord = (
    record: Model,
    table: HorsePreparedEntityTableName,
    operation: ValidationOperation,
): PreparedHorseData => ({
    record,
    table,
    operation,
});

export const mapRecords = (
    records: Model[],
    table: HorsePreparedEntityTableName,
    operation: ValidationOperation,
): PreparedHorseData[] =>
    records.map((record) => mapRecord(record, table, operation));

const isHorsePreparedEntityTableName = (
    table: string,
): table is HorsePreparedEntityTableName => table in horsePreparedEntityTables;

const getRecordTable = (record: Model): string | undefined =>
    (record.constructor as typeof Model & { table?: string }).table;

export const mapRecordsByTable = (
    records: Model[],
    operation: ValidationOperation,
): PreparedHorseData[] =>
    records.map((record) => {
        const table = getRecordTable(record);

        if (!table || !isHorsePreparedEntityTableName(table)) {
            throw new Error(
                `Unsupported horse prepared record table: ${table}`,
            );
        }

        return mapRecord(record, table, operation);
    });

export const addPreparedDataBatch = async (
    preparedData: PreparedHorseData[],
    database: Database,
) => {
    await database.batch(...preparedData.map(({ record }) => record));
};

export const prepareHorseLocation = async ({
    location,
    userId,
    organisationID,
    database,
}: {
    location: HorsePayload['location'];
    userId: string;
    organisationID: string;
    database: Database;
}): Promise<{
    preparedLocation?: LocationModel;
    existingLocation?: LocationModel;
}> => {
    if (!location || isEmpty(location)) return {};

    const locationCollection =
        database.collections.get<LocationModel>('locations');

    const existingLocations = await locationCollection
        .query(Q.where('place_id', location.placeId))
        .fetch();

    if (existingLocations.length > 0) {
        const [existingLocation] = existingLocations;

        return { existingLocation };
    }

    const locationElement = locationCollection.prepareCreate((loc) => {
        loc.placeId = location.placeId;
        loc.name = location.name;
        loc.lat = `${location.lat}`;
        loc.lng = `${location.lng}`;
        loc.url = location.url;
        loc.utcOffset = location.utcOffset;
        loc.userId = userId;
        loc.organisationId = organisationID;
    });

    return { preparedLocation: locationElement };
};
